// src/components/pages/admin-dash/DrawGetById.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Badge from "../../ui/Badge";
import ConfirmDialog from "../../ui/ConfirmDialog";

const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(n)
    : String(n ?? "—");

const moonLabel = (m) =>
  m ? m.charAt(0) + m.slice(1).toLowerCase().replace(/(quarter|moon|crescent|gibbous)/, " $1").trim() : null;

export default function DrawGetById({ onNavigate, onShowToast }) {
  const [drawId, setDrawId] = useState("");
  const [draw, setDraw]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSearch = async () => {
    setError("");
    setDraw(null);
    if (!drawId) {
      setError("Please enter a draw ID.");
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/admin/draw/${drawId}`);
      if (!res.data) {
        setError("Draw not found.");
      } else {
        setDraw(res.data);
      }
    } catch {
      setError("Draw not found, or failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/draw/${draw.id}`);
      onShowToast?.(`Draw #${draw.id} deleted.`, "success");
      setConfirmOpen(false);
      setDraw(null);
      setDrawId("");
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Failed to delete draw.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleKey = (e) => e.key === "Enter" && handleSearch();

  const lottery = (draw?.typeLottery || "").toUpperCase();
  const nums = draw && [draw.num1, draw.num2, draw.num3, draw.num4, draw.num5];
  const hasNums = nums && nums.every((n) => n != null);

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Find draw by ID
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Quick lookup for a specific draw.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <Field
            label="Draw ID"
            type="number"
            value={drawId}
            onChange={(e) => setDrawId(e.target.value)}
            onKeyDown={handleKey}
            placeholder="e.g. 728"
            className="!mb-0"
          />
          <div className="flex items-end">
            <Button icon="search" onClick={handleSearch} loading={loading} disabled={!drawId}>Search
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      {draw && (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="lg-label mb-1">Lottery</div>
              {lottery === "MEGA_MILLIONS" ? (
                <Badge variant="gold">Mega Millions</Badge>
              ) : lottery === "POWER_BALL" ? (
                <Badge variant="blue">Powerball</Badge>
              ) : (
                <Badge variant="neutral">{lottery || "Unknown"}</Badge>
              )}
            </div>
            <div className="text-right">
              <div className="lg-label mb-1">Jackpot</div>
              <div
                className="font-semibold tabular-nums"
                style={{ color: "var(--gold)", fontSize: 22 }}
              >
                {fmtUsd(Number(draw.value))}
              </div>
            </div>
          </div>

          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-3">
            <DetailField label="Draw ID"    mono value={`#${draw.id}`} />
            <DetailField label="Date"       value={draw.date?.split?.("T")[0] || draw.date} />
            <DetailField label="Moon phase" value={moonLabel(draw.moonPhase)} />
          </div>

          {hasNums && (
            <div className="mt-5">
              <div className="lg-label mb-2">Winning numbers</div>
              <div className="flex flex-wrap items-center gap-2">
                {[...nums, draw.extraBall].map((n, i, arr) => (
                  <div
                    key={i}
                    className="inline-flex items-center justify-center rounded-full font-semibold tabular-nums"
                    style={{
                      width: 36,
                      height: 36,
                      background: i === arr.length - 1 ? "var(--gold)" : "var(--surf-2)",
                      color: i === arr.length - 1 ? "#0E2A18" : "var(--txt)",
                      border: `1px solid ${i === arr.length - 1 ? "var(--gold)" : "var(--border-2)"}`,
                      fontSize: 13,
                    }}
                  >
                    {n}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-6">
            <Button variant="danger" icon="trash" size="sm" onClick={() => setConfirmOpen(true)}>
              Delete
            </Button>
            {onNavigate && (
              <Button icon="pencil" size="sm" onClick={() => onNavigate("editDraw", { drawId: draw.id })}>
                Edit
              </Button>
            )}
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={draw ? `Delete draw #${draw.id}?` : ""}
        message="This permanently removes the draw record. This action can't be undone."
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        icon="trash"
      />
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div>
      <div className="lg-label mb-1">{label}</div>
      <div
        className={`text-[13.5px] ${mono ? "tabular-nums" : ""}`}
        style={{ color: "var(--txt)", wordBreak: "break-word" }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}
